<template>
    <!-- Owner 2026-10-15: the order detail of /seller/orders opens INLINE under its
         row (the old el-dialog is gone). Same look as the BO orders overview panel
         (OrdersNewDetail.vue), but fed by the SELLER endpoint
         GET /api/v1/bo/seller/orders/:uuid (mw.IsSeller, tenant-scoped) — the
         staff GET /orders/detail is not open to sellers. Stateless: Orders.vue
         owns the fetch + cache and passes `detail`; this panel renders and emits
         `close` / `retry`. -->
    <div class="sod-detail" data-testid="seller-order-detail">
        <div class="d-flex flex-wrap align-items-center sod-head">
            <span class="badge so-badge-lg mr-2" :class="statusClass">{{ statusText }}</span>
            <span class="font-weight-bold mr-2">{{ order.order_no || order.uuid }}</span>
            <span class="text-muted sod-meta mr-3">
                {{ channelText }} · {{ $t("seller_orders.col_placed_at") }} {{ placedAt || "-" }}
                <template v-if="boOrderId"> · BO #{{ boOrderId }}</template>
            </span>
        </div>

        <!-- Load error: message + retry -->
        <div v-if="error && !loading" class="alert alert-secondary d-flex align-items-center mb-3" data-testid="sod-error">
            <span class="mr-3">{{ error }}</span>
            <base-button size="sm" type="primary" class="ml-auto" @click="$emit('retry')">
                <i class="fa fa-redo mr-1"></i>{{ $t("seller_orders.detail_retry") }}
            </base-button>
        </div>

        <div class="row">
            <div class="col-12 col-lg-4 mb-3">
                <div class="sod-block">
                    <div class="sod-block-title">{{ $t("seller_orders.detail_address") }}</div>
                    <div class="font-weight-bold">{{ order.recipient_name || "-" }}</div>
                    <div>{{ order.recipient_phone || "-" }}</div>
                    <div class="sod-wrap" data-testid="sod-address">{{ addressText }}</div>
                    <div v-if="note" class="sod-wrap mt-2" data-testid="sod-note">
                        <span class="sod-k">{{ $t("seller_orders.detail_customer_note") }}:</span> {{ note }}
                    </div>
                </div>
            </div>
            <div class="col-12 col-lg-4 mb-3">
                <div class="sod-block">
                    <div class="sod-block-title">{{ $t("seller_orders.detail_shipping") }}</div>
                    <div><span class="sod-k">{{ $t("seller_orders.f_shipping") }}:</span> {{ order.shipping_provider || "-" }}</div>
                    <div class="sod-wrap" data-testid="sod-tracking">
                        <span class="sod-k mr-1">{{ $t("seller_orders.col_tracking") }}:</span>
                        <template v-if="trackings.length">
                            <span v-for="tn in trackings" :key="tn" class="sod-tn">
                                {{ tn }}<button
                                    type="button"
                                    class="btn btn-link p-0 ml-1 sod-copy"
                                    data-testid="sod-copy-tracking"
                                    :title="$t('seller_orders.copy')"
                                    :aria-label="$t('seller_orders.copy')"
                                    @click.stop="$emit('copy', tn)"
                                ><i class="fa fa-copy"></i></button>
                            </span>
                        </template>
                        <template v-else>-</template>
                    </div>
                    <div v-if="order.shipping_status_name">
                        <span class="sod-k">{{ $t("seller_orders.detail_carrier_status") }}:</span>
                        {{ order.shipping_status_name }}<template v-if="shippingAt"> · {{ shippingAt }}</template>
                    </div>
                    <div v-if="boPackingId"><span class="sod-k">{{ $t("seller_orders.col_packing_round") }}:</span> #{{ boPackingId }}</div>
                </div>
            </div>
            <div class="col-12 col-lg-4 mb-3">
                <div class="sod-block">
                    <div class="sod-block-title">{{ $t("seller_orders.detail_payment") }}</div>
                    <div data-testid="sod-payment-type"><span class="sod-k">{{ $t("seller_orders.col_payment_type") }}:</span> {{ paymentTypeShown }}</div>
                    <div v-for="r in paymentChannelRows" :key="r.key" :data-testid="`sod-pay-${r.key.split('.').pop()}`">
                        <span class="sod-k">{{ $t(r.key) }}:</span> {{ r.value }}
                    </div>
                    <div>
                        <span class="sod-k">{{ $t("seller_orders.col_payment_status") }}:</span>
                        <span class="badge so-badge-lg" :class="payStatusClass">{{ payStatusText }}</span>
                    </div>
                    <div v-if="payment.invoice_no"><span class="sod-k">{{ $t("seller_orders.col_invoice") }}:</span> {{ payment.invoice_no }}</div>
                    <div><span class="sod-k">{{ $t("seller_orders.detail_paid_at") }}:</span> {{ paidAt || "-" }}</div>
                </div>
            </div>
        </div>

        <!-- ============ Cancellation (spec 2026-10-16 seller-order-cancel-actor-reason §5) ============ -->
        <!-- Who cancelled + why: `cancel` of the detail (else the list row). A staff
             cancel shows the staff's own note — this is an internal page; customers
             only ever see the token text (spec D2). -->
        <div v-if="cancelInfo" class="sod-block sod-cancel mb-3" data-testid="sod-cancel">
            <div class="sod-block-title">{{ $t("seller_orders.cancel_block_title") }}</div>
            <div class="row">
                <div class="col-12 col-md-6 col-xl-3" data-testid="sod-cancel-actor">
                    <span class="sod-k">{{ $t("seller_orders.cancel_actor") }}:</span>
                    <span class="badge so-badge-lg ml-1" :class="cancelBadgeClass">{{ cancelInfo.actor }}</span>
                </div>
                <div class="col-12 col-md-6 col-xl-3" data-testid="sod-cancel-type">
                    <span class="sod-k">{{ $t("seller_orders.cancel_type") }}:</span> {{ cancelInfo.typeLabel || "-" }}
                </div>
                <div class="col-12 col-md-6 col-xl-4 sod-wrap" data-testid="sod-cancel-reason">
                    <span class="sod-k">{{ $t("seller_orders.cancel_reason") }}:</span> {{ cancelInfo.reasonText || "-" }}
                </div>
                <div class="col-12 col-md-6 col-xl-2" data-testid="sod-cancel-time">
                    <span class="sod-k">{{ $t("seller_orders.cancel_time") }}:</span> {{ cancelInfo.at || "-" }}
                </div>
            </div>
        </div>

        <!-- Totals: one line -->
        <div class="sod-totals mb-3" data-testid="sod-totals">
            <span>{{ $t("seller_orders.detail_amount_items") }} {{ money(order.amount) }}</span>
            <span class="sod-dot">·</span>
            <span>{{ $t("seller_orders.detail_discount") }} {{ money(order.discount) }}</span>
            <span class="sod-dot">·</span>
            <span>{{ $t("seller_orders.detail_shipping_cost") }} {{ money(order.shipping_cost) }}</span>
            <span class="sod-dot">·</span>
            <span class="font-weight-bold">{{ $t("seller_orders.col_amount") }} {{ money(order.pay_amount) }}</span>
        </div>

        <div class="row">
            <div class="col-12 col-xl-8">
                <!-- ============ Items ============ -->
                <div class="sod-section">
                    <div class="sod-section-title">
                        {{ $t("seller_orders.detail_items") }}<template v-if="!loading && !error"> ({{ items.length }})</template>
                    </div>
                    <template v-if="loading">
                        <div v-for="n in 2" :key="'si' + n" class="sod-line">
                            <div class="sod-thumb sod-skel"></div>
                            <div class="flex-grow-1"><div class="sod-skel sod-skel-text w-50"></div></div>
                        </div>
                    </template>
                    <template v-else-if="!error">
                        <div v-if="!items.length" class="text-muted py-2">{{ $t("seller_orders.detail_no_items") }}</div>
                        <div v-for="(it, i) in items" :key="'it' + i" class="sod-line" data-testid="sod-item">
                            <!-- Picture: SKU → product → bundle pick (itemImageCandidates;
                                 a failed URL falls through to the next). A real <button>
                                 opening the large-image preview (owner 2026-10-15); the
                                 no-image placeholder is inert. -->
                            <button
                                v-if="itemSrc(it)"
                                type="button"
                                class="sod-thumb-btn"
                                data-testid="sod-item-thumb"
                                :aria-label="$t('app_catalog.image_preview_open', { name: it.product_name || '' })"
                                @click.stop="openPreview(itemSrc(it), it.product_name, $event)"
                            >
                                <img
                                    :src="itemSrc(it)"
                                    :alt="it.product_name"
                                    loading="lazy"
                                    class="sod-thumb"
                                    @error="onImgError(itemSrc(it))"
                                />
                            </button>
                            <div v-else class="sod-thumb sod-thumb-empty" data-testid="sod-item-thumb-empty"><i class="fa fa-image"></i></div>
                            <div class="flex-grow-1 sod-line-text">
                                <div class="font-weight-bold sod-wrap">{{ it.product_name || "-" }}</div>
                                <small v-if="it.sku_name || it.product_code" class="text-muted d-block sod-wrap">
                                    {{ it.sku_name }}<template v-if="it.sku_name && it.product_code"> · </template>{{ it.product_code }}
                                </small>
                            </div>
                            <div class="text-right sod-qty">
                                <div class="font-weight-bold">× {{ it.quantity }}</div>
                                <small class="text-muted d-block">{{ money(it.unit_price) }}</small>
                                <small class="d-block">{{ money(it.line_total) }}</small>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- ============ WMS pack list (once the order is in the BO) ============ -->
                <div class="sod-section">
                    <div class="sod-section-title">
                        {{ $t("seller_orders.detail_wms_items") }}<template v-if="!loading && !error"> ({{ wmsItems.length }})</template>
                    </div>
                    <template v-if="!loading && !error">
                        <div v-if="!wmsItems.length" class="text-muted py-2">{{ $t("seller_orders.detail_no_wms_items") }}</div>
                        <div v-for="(w, i) in wmsItems" :key="'w' + i" class="sod-line" data-testid="sod-wms-item">
                            <button
                                v-if="showImg(wmsImage(w))"
                                type="button"
                                class="sod-thumb-btn"
                                data-testid="sod-wms-thumb"
                                :aria-label="$t('app_catalog.image_preview_open', { name: w.wms_product_name || '' })"
                                @click.stop="openPreview(wmsImage(w), w.wms_product_name, $event)"
                            >
                                <img
                                    :src="wmsImage(w)"
                                    :alt="w.wms_product_name"
                                    loading="lazy"
                                    class="sod-thumb"
                                    @error="onImgError(wmsImage(w))"
                                />
                            </button>
                            <div v-else class="sod-thumb sod-thumb-empty" data-testid="sod-wms-thumb-empty"><i class="fa fa-image"></i></div>
                            <div class="flex-grow-1 sod-line-text">
                                <div class="font-weight-bold sod-wrap">{{ w.wms_product_name || "-" }}</div>
                                <small v-if="w.barcode" class="text-muted d-block">{{ w.barcode }}</small>
                            </div>
                            <div class="text-right sod-qty">
                                <div class="font-weight-bold">× {{ w.pickup_quantity }}</div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- ============ Timeline ============ -->
            <div class="col-12 col-xl-4">
                <div class="sod-section" data-testid="sod-timeline">
                    <div class="sod-section-title">{{ $t("seller_orders.detail_timeline") }}</div>
                    <div v-if="!timeline.length" class="text-muted py-2">-</div>
                    <ol v-else class="sod-tl">
                        <li v-for="e in timeline" :key="e.key" class="sod-tl-item">
                            <div class="sod-tl-at">{{ e.at }}</div>
                            <div class="sod-wrap">
                                {{ e.labelKey ? $t(e.labelKey) : e.text }}<template v-if="e.place"> · <span class="text-muted">{{ e.place }}</span></template>
                            </div>
                        </li>
                    </ol>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end">
            <!-- spec §5: shown only when the API says `cancellable` and the viewer
                 has the full order role (Orders.vue canCancelRow). -->
            <base-button v-if="canCancel" size="sm" type="danger" class="mr-2" data-testid="sod-cancel-btn" @click="$emit('cancel')">
                <i class="fa fa-times-circle mr-1"></i>{{ $t("seller_orders.cancel_btn") }}
            </base-button>
            <base-button size="sm" type="secondary" data-testid="sod-close" @click="$emit('close')">
                <i class="fa fa-chevron-up mr-1"></i>{{ $t("seller_orders.detail_hide") }}
            </base-button>
        </div>

        <!-- Large-image preview of an item / WMS picture (owner 2026-10-15). -->
        <image-preview-lightbox :preview="preview" @close="preview = null" />
    </div>
</template>

<script>
import ImagePreviewLightbox from "@/components/ImagePreview/ImagePreviewLightbox.vue";
import { previewOf } from "@/utils/imagePreview";
import { IMAGE_PREFIX, imageUrl } from "@/utils/orderDetailPanel";
import { statusBadgeClass } from "@/utils/sellerOrderFilters";
import { channelBadge } from "@/utils/sellerPackingRound";
import { cancelBadge, cancelView } from "@/utils/orderCancelReason";
import { formatBangkok, itemImageCandidates, sellerAddressText, sellerItemsOf, sellerPaymentChannelLabel, sellerPaymentChannelRows, sellerTimeline, sellerWmsItemsOf, trackingNumbersOf } from "@/utils/sellerOrderDetail";

export default {
    name: "SellerOrderDetail",
    components: { ImagePreviewLightbox },
    props: {
        // The list row (always there) and the detail response `data` (null until loaded).
        row: { type: Object, required: true },
        detail: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        error: { type: String, default: "" },
        apiBase: { type: String, default: "" },
        // Worked out by Orders.vue with the same helpers as the table cells.
        paymentTypeText: { type: String, default: "-" },
        payStatusText: { type: String, default: "" },
        payStatusClass: { type: String, default: "badge-secondary" },
        // Orders.vue: `cancellable` (detail wins over row) AND Auth/isFullOrder.
        canCancel: { type: Boolean, default: false }
    },
    data() {
        return {
            // url -> true once the <img> fired `error` (then the placeholder shows).
            broken: {},
            // Large-image preview: { src, caption, trigger } or null (closed).
            preview: null
        };
    },
    computed: {
        // The row has the list columns; the detail's header adds address, totals, scans.
        order() {
            const head = (this.detail && this.detail.order) || {};
            return Object.assign({}, this.row || {}, head);
        },
        payment() {
            return (this.detail && this.detail.payment) || {};
        },
        // Owner 2026-10-26: the real 2C2P channel + its details (bo-api
        // payment.channel from 2C2P's notice) — the list's label otherwise.
        paymentTypeShown() {
            return sellerPaymentChannelLabel(this.payment.channel, this.$i18n && this.$i18n.locale) || this.paymentTypeText;
        },
        paymentChannelRows() {
            return sellerPaymentChannelRows(this.payment.channel);
        },
        items() {
            return sellerItemsOf(this.detail);
        },
        wmsItems() {
            return sellerWmsItemsOf(this.detail);
        },
        trackings() {
            return trackingNumbersOf(this.detail && Array.isArray(this.detail.tracking_numbers) ? this.detail : this.order);
        },
        timeline() {
            return sellerTimeline(this.order, this.detail && this.detail.tracking_events);
        },
        addressText() {
            return sellerAddressText(this.order);
        },
        note() {
            return String(this.order.customer_note || "").trim();
        },
        statusClass() {
            return statusBadgeClass(this.order.order_status);
        },
        statusText() {
            const key = `seller_orders.status_${this.order.order_status}`;
            const label = this.$t(key);
            return label === key ? String(this.order.order_status) : label;
        },
        channelText() {
            return this.$t(channelBadge(this.order.source_channel || this.order.channel || "").labelKey);
        },
        placedAt() {
            return formatBangkok(this.order.created_at);
        },
        paidAt() {
            return formatBangkok(this.order.payment_at);
        },
        shippingAt() {
            return formatBangkok(this.order.shipping_status_date);
        },
        boOrderId() {
            return Number((this.detail && this.detail.bo_order_id) || this.row.bo_order_id || 0);
        },
        // The cancel object of a cancelled order: the detail's own, else the row's (spec §4).
        cancelObj() {
            if (Number(this.order.order_status) !== 140) return null;
            const c = (this.detail && this.detail.cancel) || this.row.cancel;
            return c && typeof c === "object" ? c : null;
        },
        cancelInfo() {
            return cancelView(this.cancelObj, (k, p) => this.$t(k, p));
        },
        cancelBadgeClass() {
            const b = cancelBadge(this.cancelObj, (k, p) => this.$t(k, p));
            return b ? b.badgeClass : "badge-danger";
        },
        boPackingId() {
            return Number((this.detail && this.detail.bo_packing_id) || this.row.bo_packing_id || 0);
        }
    },
    methods: {
        // The first picture of the line that has not failed to load ("" = placeholder).
        itemSrc(it) {
            return itemImageCandidates(it).find(u => !this.broken[u]) || "";
        },
        openPreview(src, caption, evt) {
            this.preview = previewOf(src, caption, evt && evt.currentTarget);
        },
        wmsImage(w) {
            return imageUrl(this.apiBase, IMAGE_PREFIX.wms, w && w.image);
        },
        showImg(url) {
            return !!url && !this.broken[url];
        },
        onImgError(url) {
            if (url) this.$set(this.broken, url, true);
        },
        money(value) {
            const n = Number(value || 0);
            return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }
};
</script>

<style scoped>
.sod-detail {
    padding: 12px 16px;
    background: #f8f9fe;
    border-left: 3px solid #1c1026;
    color: #525f7f;
    font-size: 0.875rem;
    line-height: 1.5;
    text-align: left;
    white-space: normal;
    word-break: normal;
}
.sod-head {
    margin-bottom: 12px;
}
.sod-meta {
    font-size: 0.8125rem;
}
.sod-block,
.sod-totals,
.sod-section {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 10px 12px;
}
.sod-block {
    height: 100%;
}
.sod-cancel {
    height: auto;
    border-left: 3px solid #f5365c;
}
/* A long staff name ("ยกเลิกโดยพนักงาน (ชื่อ นามสกุล)") wraps inside the
   badge instead of overflowing the column (the theme's .badge is nowrap). */
.sod-cancel .badge {
    white-space: normal;
    text-align: left;
    line-height: 1.4;
}
.sod-section {
    margin-bottom: 12px;
}
.sod-block-title,
.sod-section-title {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #6c757d;
    margin-bottom: 6px;
}
.sod-k {
    color: #6c757d;
}
.sod-wrap {
    overflow-wrap: anywhere;
}
.sod-tn {
    display: inline-block;
    margin-right: 10px;
    white-space: nowrap;
}
.sod-dot {
    margin: 0 8px;
    color: #6c757d;
}
.sod-line {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-top: 1px solid #f1f3f9;
}
.sod-section-title + .sod-line,
.sod-section-title + div + .sod-line {
    border-top: 0;
}
.sod-thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 6px;
    margin-right: 12px;
    background: #e9ecef;
}
/* The picture is a button (opens the large-image preview): no chrome of its
   own, the 56px image is the whole target. Same as Products.vue's thumb. */
.sod-thumb-btn {
    flex: 0 0 56px;
    display: inline-block;
    padding: 0;
    margin-right: 12px;
    border: 0;
    background: transparent;
    border-radius: 6px;
    line-height: 0;
    cursor: zoom-in;
}
.sod-thumb-btn .sod-thumb {
    margin-right: 0;
    border: 1px solid transparent;
}
.sod-thumb-btn:hover .sod-thumb {
    border-color: currentColor;
}
.sod-thumb-btn:focus {
    outline: none;
}
.sod-thumb-btn:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
}
.sod-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 1.25rem;
}
.sod-line-text {
    min-width: 0;
}
.sod-qty {
    flex: 0 0 auto;
    margin-left: 12px;
    white-space: nowrap;
}
.sod-copy {
    color: #525f7f;
    font-size: 0.8rem;
    vertical-align: baseline;
    line-height: 1;
}
.sod-copy:hover,
.sod-copy:focus {
    color: #1c1026;
}
.sod-tl {
    list-style: none;
    margin: 0;
    padding: 0 0 0 14px;
    border-left: 2px solid #e9ecef;
}
.sod-tl-item {
    position: relative;
    padding: 0 0 10px 8px;
}
.sod-tl-item::before {
    content: "";
    position: absolute;
    left: -20px;
    top: 6px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #8898aa;
}
.sod-tl-item:last-child::before {
    background: #1c1026;
    border-color: #1c1026;
}
.sod-tl-at {
    font-size: 0.75rem;
    color: #6c757d;
}
.sod-skel {
    background: linear-gradient(90deg, #e9ecef 25%, #f4f5f7 50%, #e9ecef 75%);
    background-size: 200% 100%;
    animation: sod-skel 1.2s ease-in-out infinite;
}
.sod-skel-text {
    height: 12px;
    border-radius: 4px;
}
@keyframes sod-skel {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
